import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { MoneyInput } from '../ui/money-input';
import { CharCounter } from '../ui/char-counter';
import InlineDatePicker from '../InlineDatePicker';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  CalendarDays,
  DollarSign,
  AlertCircle,
  Plus,
  Trash2,
  Loader2,
  CreditCard,
  Banknote,
  CheckCircle,
  Receipt,
  Wallet,
  FileText,
  TrendingUp,
  Info,
  ChevronDown,
} from 'lucide-react';
import { PaymentInstallment } from '../../types';
import { Customer } from '../../services/clientsService';
import cashBoxService, { CashBox } from '../../services/cashBoxService';
import customerCurrentAccountService, { CustomerCurrentAccount } from '../../services/customerCurrentAccountService';
import { cashBoxMatchesAccountingType } from '../../utils/accountingType';

interface MarkAsPaidModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (paymentData: PaymentConfirmationData) => Promise<void>;
  installment: PaymentInstallment | null;
  customer: Customer | null;
}

export interface PaymentConfirmationData {
  installmentId: number;
  paymentDate: string;
  accountingDate: string;
  accountingType: string;
  currency: string;
  exchangeRate?: number;
  paymentSource: 'CASHBOX' | 'CURRENT_ACCOUNT';
  cashBoxId?: number;
  customerCurrentAccountId?: number;
  perceptions: number;
  comments?: string;
  paymentMethods?: { method: string; amount: number; reference?: string }[];
}

const PAYMENT_METHODS = [
  { value: 'EFECTIVO', label: 'Efectivo' },
  { value: 'TRANSFERENCIA', label: 'Transferencia' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'TARJETA_DEBITO', label: 'Tarjeta de Débito' },
  { value: 'TARJETA_CREDITO', label: 'Tarjeta de Crédito' },
  { value: 'OTRO', label: 'Otro' },
];

const getAvailablePaymentMethods = (cashBox: CashBox | undefined): typeof PAYMENT_METHODS => {
  if (!cashBox) return [];
  if (cashBox.accountType.toUpperCase() === 'EFECTIVO') {
    return PAYMENT_METHODS.filter(m => m.value === 'EFECTIVO');
  }
  return PAYMENT_METHODS.filter(m => m.value !== 'EFECTIVO');
};

const roundTo2 = (n: number) => Math.round(n * 100) / 100;

// Convierte string 'YYYY-MM-DD' → Date local (sin desfase de zona)
function parseLocalDate(str: string): Date {
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
}

// Convierte Date → 'YYYY-MM-DD'
function toDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}



// ── Encabezado de sección ──────────────────────────────────────────────────
function SectionHeader({ icon: Icon, title }: { icon: React.ElementType; title: string }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <Icon className="h-4 w-4 text-primary" />
      <p className="text-sm font-semibold text-foreground">{title}</p>
    </div>
  );
}

// ── Celda de dato de sólo lectura ─────────────────────────────────────────
function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
      <div className="text-sm font-medium text-foreground">{children}</div>
    </div>
  );
}

export default function MarkAsPaidModal({
  isOpen,
  onClose,
  onConfirm,
  installment,
  customer,
}: MarkAsPaidModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [paymentSource, setPaymentSource] = useState<'CASHBOX' | 'CURRENT_ACCOUNT'>('CASHBOX');
  const [paymentDate, setPaymentDate] = useState('');
  const [accountingDate, setAccountingDate] = useState('');
  const [cashBoxId, setCashBoxId] = useState<string>('');
  const [perceptions, setPerceptions] = useState<number | null>(0);
  const [comments, setComments] = useState('');
  const [paymentMethods, setPaymentMethods] = useState<{ method: string; amount: number | null; reference?: string }[]>([]);
  const [calculatedMora, setCalculatedMora] = useState(0);

  const [cashBoxes, setCashBoxes] = useState<CashBox[]>([]);
  const [customerAccount, setCustomerAccount] = useState<CustomerCurrentAccount | null>(null);
  const [loadingAccount, setLoadingAccount] = useState(false);



  useEffect(() => {
    if (!isOpen || !customer) return;
    const today = toDateString(new Date());
    setPaymentDate(today);
    setAccountingDate(today);
    setError(null);

    cashBoxService.getAllCashBoxes()
      .then(boxes => setCashBoxes(boxes))
      .catch(() => setError('Error al cargar las cajas disponibles'));

    setLoadingAccount(true);
    customerCurrentAccountService.getCustomerAccountBalance(customer.id)
      .then(account => setCustomerAccount(account))
      .catch(() => setCustomerAccount(null))
      .finally(() => setLoadingAccount(false));
  }, [isOpen, customer]);

  // Recalcular mora al cambiar la fecha de pago
  useEffect(() => {
    if (!installment?.moraRate || !installment?.moraFrequency || !paymentDate || !installment.dueDate) {
      setCalculatedMora(0);
      return;
    }
    const frequencyDays: Record<string, number> = { DAILY: 1, MONTHLY: 30, ANNUAL: 365 };
    const days = frequencyDays[installment.moraFrequency] ?? 30;
    const due = parseLocalDate(installment.dueDate);
    const paid = parseLocalDate(paymentDate);
    const daysLate = Math.max(0, Math.floor((paid.getTime() - due.getTime()) / 86400000));
    setCalculatedMora(installment.installmentAmount * (installment.moraRate / 100) * (daysLate / days));
  }, [paymentDate, installment]);

  const getCustomerName = (c: Customer) =>
    c.type === 'PERSON' ? `${c.firstName} ${c.lastName}` : c.businessName;

  const formatCurrency = (amount: number, currency: string) => {
    const symbol = currency.toUpperCase() === 'USD' ? 'USD' : '$';
    return `${symbol} ${amount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const addPaymentMethod = () =>
    setPaymentMethods(prev => [...prev, { method: '', amount: null, reference: '' }]);

  const updatePaymentMethod = (
    index: number,
    field: 'method' | 'amount' | 'reference',
    value: string | number | null,
  ) => setPaymentMethods(prev => prev.map((pm, i) => (i === index ? { ...pm, [field]: value } : pm)));

  const removePaymentMethod = (index: number) =>
    setPaymentMethods(prev => prev.filter((_, i) => i !== index));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!installment) return;

    if (!paymentDate) { setError('La fecha de pago es obligatoria'); return; }
    if (!accountingDate) { setError('La fecha de contabilización es obligatoria'); return; }

    const requiredAmount = (installment.adjustedAmount || installment.installmentAmount) + calculatedMora;

    if (paymentSource === 'CASHBOX') {
      if (!cashBoxId) { setError('Debe seleccionar una caja'); return; }
      if (paymentMethods.length === 0) { setError('Debe agregar al menos un método de pago'); return; }
      if (paymentMethods.some(pm => !pm.method)) { setError('Todos los métodos de pago deben tener un tipo seleccionado'); return; }
      const totalPaymentAmount = paymentMethods.reduce((s, pm) => s + (pm.amount ?? 0), 0);
      if (roundTo2(totalPaymentAmount) !== roundTo2(requiredAmount)) {
        setError(`La suma (${formatCurrency(totalPaymentAmount, installment.currency)}) debe ser igual al monto a cobrar (${formatCurrency(requiredAmount, installment.currency)})`);
        return;
      }
    } else if (paymentSource === 'CURRENT_ACCOUNT') {
      if (!customerAccount) { setError('No se pudo cargar la cuenta corriente del cliente'); return; }
      if (customerAccount.balance < requiredAmount) {
        setError(`Saldo insuficiente (${formatCurrency(customerAccount.balance, installment.currency)}) para cubrir la cuota (${formatCurrency(requiredAmount, installment.currency)})`);
        return;
      }
    }

    const perceptionsValue = perceptions ?? 0;
    if (perceptionsValue < 0) { setError('Las percepciones no pueden ser negativas'); return; }

    try {
      setLoading(true);
      setError(null);
      const refs = paymentMethods
        .filter(pm => pm.method && pm.method !== 'EFECTIVO' && pm.reference?.trim())
        .map(pm => `${pm.method}: ${pm.reference!.trim()}`);
      const refNote = refs.length > 0 ? `Ref: ${refs.join(', ')}` : '';
      // La columna comments es VARCHAR(255): truncamos el resultado final (comentario + referencias).
      const finalComments = ([comments.trim(), refNote].filter(Boolean).join(' | ') || '')
        .slice(0, 255) || undefined;

      await onConfirm({
        installmentId: installment.installmentId,
        paymentDate,
        accountingDate,
        accountingType: installment.accountingType,
        currency: installment.currency,
        exchangeRate: installment.exchangeRate,
        paymentSource,
        cashBoxId: paymentSource === 'CASHBOX' ? parseInt(cashBoxId) : undefined,
        customerCurrentAccountId: paymentSource === 'CURRENT_ACCOUNT' && customerAccount ? customerAccount.id : undefined,
        perceptions: perceptionsValue,
        comments: finalComments,
        paymentMethods: paymentSource === 'CASHBOX'
          ? paymentMethods.map(pm => ({ method: pm.method, amount: pm.amount ?? 0, reference: pm.reference }))
          : undefined,
      });
      handleClose();
    } catch {
      setError('Error al procesar el pago. Por favor, intentá nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    setPaymentSource('CASHBOX');
    setPaymentDate('');
    setAccountingDate('');
    setCashBoxId('');
    setPerceptions(0);
    setCalculatedMora(0);
    setComments('');
    setPaymentMethods([]);
    setError(null);

    onClose();
  };

  if (!installment || !customer) return null;

  const filteredCashBoxes = cashBoxes.filter(cb => {
    if (!cb.isActive) return false;
    if (cb.currency.toUpperCase() !== installment.currency.toUpperCase()) return false;
    return cashBoxMatchesAccountingType(cb.accountingType, installment.accountingType);
  });

  const totalPaymentAmount = paymentMethods.reduce((s, pm) => s + (pm.amount ?? 0), 0);
  const selectedCashBox = cashBoxes.find(cb => cb.id.toString() === cashBoxId);
  const availablePaymentMethods = getAvailablePaymentMethods(selectedCashBox);
  const requiredAmount = roundTo2((installment.adjustedAmount || installment.installmentAmount) + calculatedMora);
  const amountsMatch = roundTo2(totalPaymentAmount) === requiredAmount;
  const hasCAC = installment.adjustedAmount && installment.adjustedAmount !== installment.installmentAmount;
  const hasMora = !!installment.moraRate && calculatedMora > 0;

  return (
    <>
    <Dialog open={isOpen} onOpenChange={open => { if (!open) handleClose(); }}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto scrollbar-custom bg-card border-border p-0">

        {/* ── Header ── */}
        <DialogHeader className="px-6 py-4 border-b border-border">
          <DialogTitle className="flex items-center gap-2.5 text-lg font-bold text-foreground">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
              <Receipt className="h-4 w-4 text-primary" />
            </div>
            Registrar Cobro de Cuota
            <span className="ml-1 text-sm font-normal text-muted-foreground">
              · {getCustomerName(customer)}
            </span>
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-6">

          {/* ══ Resumen de la cuota ══════════════════════════════════════ */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">

            {/* Datos básicos */}
            <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-3">
              <SectionHeader icon={Info} title="Detalle de la Cuota" />
              <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                <InfoRow label="Vencimiento">
                  {installment.dueDate
                    ? parseLocalDate(installment.dueDate).toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' })
                    : '—'}
                </InfoRow>
                <InfoRow label="Moneda">
                  <Badge variant="outline" className="text-xs font-semibold">
                    {installment.currency.toUpperCase()}
                  </Badge>
                </InfoRow>
                <InfoRow label="Contabilidad">
                  <Badge variant="outline" className="text-xs">
                    Tipo {installment.accountingType}
                  </Badge>
                </InfoRow>
                {installment.exchangeRate && installment.exchangeRate !== 1 && (
                  <InfoRow label="Tipo de Cambio">
                    <span className="flex items-center gap-1">
                      <DollarSign className="h-3 w-3 text-muted-foreground" />
                      {installment.exchangeRate.toFixed(2)}
                    </span>
                  </InfoRow>
                )}
              </div>
            </div>

            {/* Monto */}
            <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 flex flex-col justify-between">
              <SectionHeader icon={Wallet} title="Monto a Cobrar" />
              <div>
                {hasCAC && (
                  <p className="text-xs text-muted-foreground line-through tabular-nums">
                    {formatCurrency(installment.installmentAmount, installment.currency)}
                  </p>
                )}
                <p className="text-2xl font-bold tabular-nums text-foreground">
                  {formatCurrency(installment.adjustedAmount || installment.installmentAmount, installment.currency)}
                </p>
                {hasCAC && (
                  <p className="mt-0.5 text-xs text-primary">Ajustado por índice CAC</p>
                )}

                {hasMora && (
                  <div className="mt-3 space-y-1.5 border-t border-primary/20 pt-3">
                    <p className="text-xs font-semibold text-destructive">
                      + Mora ({installment.moraRate}%{' '}
                      {installment.moraFrequency === 'DAILY' ? 'diario' : installment.moraFrequency === 'MONTHLY' ? 'mensual' : 'anual'})
                    </p>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div>
                        <p className="text-muted-foreground">Base</p>
                        <p className="font-semibold tabular-nums">{formatCurrency(installment.installmentAmount, installment.currency)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Mora</p>
                        <p className="font-semibold tabular-nums text-destructive">+{formatCurrency(calculatedMora, installment.currency)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Total</p>
                        <p className="font-bold tabular-nums text-destructive">{formatCurrency(requiredAmount, installment.currency)}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="border-t border-border" />

          {/* ══ Fechas ═══════════════════════════════════════════════════ */}
          <div>
            <SectionHeader icon={CalendarDays} title="Fechas" />
            <div className="grid gap-4 md:grid-cols-2">
              <InlineDatePicker
                id="paymentDate"
                label={<>Fecha de Pago <span className="text-xs text-red-500">(Obligatorio)</span></>}
                value={paymentDate}
                onChange={setPaymentDate}
                hint="Cuándo recibiste el dinero del cliente."
              />
              <InlineDatePicker
                id="accountingDate"
                label={<>Fecha de Contabilización <span className="text-xs text-red-500">(Obligatorio)</span></>}
                value={accountingDate}
                onChange={setAccountingDate}
                hint="Puede coincidir con la fecha de pago."
              />
            </div>
          </div>

          <div className="border-t border-border" />

          {/* ══ Origen del pago ══════════════════════════════════════════ */}
          <div>
            <SectionHeader icon={Wallet} title="Origen del Pago" />

            <div className="grid gap-3 md:grid-cols-2">
              <button
                type="button"
                onClick={() => { setPaymentSource('CASHBOX'); setPaymentMethods([]); }}
                className={`rounded-xl border-2 p-3.5 text-left transition-all ${
                  paymentSource === 'CASHBOX'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/40 hover:bg-muted/40'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                    paymentSource === 'CASHBOX' ? 'bg-primary/10' : 'bg-muted'
                  }`}>
                    <Banknote className={`h-4 w-4 ${paymentSource === 'CASHBOX' ? 'text-primary' : 'text-muted-foreground'}`} />
                  </div>
                  <div className="flex-1">
                    <p className={`text-sm font-semibold ${paymentSource === 'CASHBOX' ? 'text-primary' : 'text-foreground'}`}>
                      Caja
                    </p>
                    <p className="text-xs text-muted-foreground">Efectivo, transferencia o banco</p>
                  </div>
                  {paymentSource === 'CASHBOX' && <CheckCircle className="h-4 w-4 shrink-0 text-primary" />}
                </div>
              </button>

              <button
                type="button"
                onClick={() => { setPaymentSource('CURRENT_ACCOUNT'); setPaymentMethods([]); setCashBoxId(''); }}
                className={`rounded-xl border-2 p-3.5 text-left transition-all ${
                  paymentSource === 'CURRENT_ACCOUNT'
                    ? 'border-success bg-success/5'
                    : 'border-border hover:border-success/40 hover:bg-muted/40'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                    paymentSource === 'CURRENT_ACCOUNT' ? 'bg-success/10' : 'bg-muted'
                  }`}>
                    <CreditCard className={`h-4 w-4 ${paymentSource === 'CURRENT_ACCOUNT' ? 'text-success' : 'text-muted-foreground'}`} />
                  </div>
                  <div className="flex-1">
                    <p className={`text-sm font-semibold ${paymentSource === 'CURRENT_ACCOUNT' ? 'text-success' : 'text-foreground'}`}>
                      Cuenta Corriente
                    </p>
                    <p className="text-xs text-muted-foreground">Descontar del saldo disponible</p>
                  </div>
                  {paymentSource === 'CURRENT_ACCOUNT' && <CheckCircle className="h-4 w-4 shrink-0 text-success" />}
                </div>
              </button>
            </div>

            {/* Info cuenta corriente */}
            {paymentSource === 'CURRENT_ACCOUNT' && (
              <div className="mt-3 overflow-hidden rounded-xl border border-border bg-muted/30">
                {loadingAccount ? (
                  <div className="flex items-center justify-center gap-2 py-6">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Cargando saldo...</span>
                  </div>
                ) : customerAccount ? (
                  <div className="divide-y divide-border">
                    <div className="flex items-center justify-between px-4 py-3">
                      <span className="text-sm text-muted-foreground">Saldo actual</span>
                      <span className="text-sm font-bold tabular-nums text-amount">
                        {formatCurrency(customerAccount.balance, installment.currency)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between px-4 py-3">
                      <span className="text-sm text-muted-foreground">A descontar</span>
                      <span className="text-sm font-bold tabular-nums text-destructive">
                        − {formatCurrency(requiredAmount, installment.currency)}
                      </span>
                    </div>
                    <div className={`flex items-center justify-between px-4 py-3 ${
                      customerAccount.balance - requiredAmount < 0 ? 'bg-destructive/5' : 'bg-success/5'
                    }`}>
                      <span className="text-sm font-semibold text-foreground">Saldo resultante</span>
                      <span className={`text-sm font-bold tabular-nums ${
                        customerAccount.balance - requiredAmount >= 0 ? 'text-amount' : 'text-destructive'
                      }`}>
                        {formatCurrency(customerAccount.balance - requiredAmount, installment.currency)}
                      </span>
                    </div>
                    {customerAccount.balance < requiredAmount && (
                      <div className="flex items-start gap-2 px-4 py-3 bg-destructive/5">
                        <AlertCircle className="h-4 w-4 shrink-0 text-destructive mt-0.5" />
                        <p className="text-xs text-destructive">
                          Saldo insuficiente para cubrir el monto de la cuota.
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-start gap-2 px-4 py-3 bg-warning/5">
                    <AlertCircle className="h-4 w-4 shrink-0 text-warning mt-0.5" />
                    <p className="text-xs text-warning-foreground">
                      No se pudo cargar la información de la cuenta corriente.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Selector de caja */}
            {paymentSource === 'CASHBOX' && (
              <div className="mt-3 space-y-1.5">
                <Label className="text-xs font-medium text-foreground sm:text-sm">
                  Caja <span className="text-xs text-red-500">(Obligatorio)</span>
                </Label>
                <Select value={cashBoxId} onValueChange={v => { setCashBoxId(v); setPaymentMethods([]); }}>
                  <SelectTrigger className="rounded-lg bg-background border-border">
                    <SelectValue placeholder="Seleccioná una caja..." />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredCashBoxes.length > 0 ? (
                      filteredCashBoxes.map(cb => (
                        <SelectItem key={cb.id} value={cb.id.toString()}>
                          {cb.name}
                          <span className="ml-1.5 text-muted-foreground text-xs">({cb.accountType})</span>
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="none" disabled>
                        No hay cajas para {installment.currency} — Tipo {installment.accountingType}
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* ══ Métodos de pago ══════════════════════════════════════════ */}
          {paymentSource === 'CASHBOX' && (
            <>
              <div className="border-t border-border" />
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-primary" />
                    <p className="text-sm font-semibold text-foreground">Métodos de Pago</p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addPaymentMethod}
                    disabled={!cashBoxId}
                    className="gap-1.5 rounded-lg h-8 text-xs"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Agregar
                  </Button>
                </div>

                {paymentMethods.length === 0 ? (
                  <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border bg-muted/20 py-6 text-center">
                    <p className="text-sm text-muted-foreground">
                      {cashBoxId
                        ? 'Hacé clic en "Agregar" para registrar los métodos de pago.'
                        : 'Primero seleccioná una caja para agregar métodos de pago.'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {/* Encabezado de columnas */}
                    <div className="grid grid-cols-[1fr_1fr_36px] gap-2 px-1">
                      <p className="text-xs font-medium text-muted-foreground">Tipo</p>
                      <p className="text-xs font-medium text-muted-foreground">Monto</p>
                      <span />
                    </div>

                    {paymentMethods.map((pm, index) => (
                      <div key={index} className="space-y-2">
                        <div className="grid grid-cols-[1fr_1fr_36px] items-start gap-2">
                          <Select
                            value={pm.method}
                            onValueChange={value => updatePaymentMethod(index, 'method', value)}
                          >
                            <SelectTrigger className="rounded-lg bg-background border-border h-10">
                              <SelectValue placeholder="Seleccioná..." />
                            </SelectTrigger>
                            <SelectContent>
                              {availablePaymentMethods.map(m => (
                                <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>

                          <div className="space-y-1.5">
                            <MoneyInput
                              currency={installment.currency}
                              value={pm.amount !== null ? pm.amount.toString() : ''}
                              onValueChange={value => updatePaymentMethod(index, 'amount', value)}
                              placeholder="0,00"
                            />
                            {/* Quick percentage buttons */}
                            <div className="flex items-center gap-1">
                              <span className="text-[10px] text-muted-foreground shrink-0">Restante:</span>
                              {[25, 50, 75, 100].map((pct) => {
                                const otherMethodsTotal = paymentMethods.reduce(
                                  (s, other, i) => i === index ? s : s + (other.amount ?? 0), 0
                                );
                                const remainingForThis = Math.max(0, requiredAmount - otherMethodsTotal);
                                const pctAmount = remainingForThis * (pct / 100);
                                return (
                                  <button
                                    key={pct}
                                    type="button"
                                    onClick={() => updatePaymentMethod(index, 'amount', Math.round(pctAmount * 100) / 100)}
                                    disabled={remainingForThis <= 0}
                                    className="px-1.5 py-0.5 text-[10px] font-medium rounded-md border border-border bg-muted/50 text-muted-foreground hover:bg-primary/10 hover:text-primary hover:border-primary/40 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                  >
                                    {pct}%
                                  </button>
                                );
                              })}
                            </div>
                          </div>

                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removePaymentMethod(index)}
                            className="h-9 w-9 text-destructive hover:text-destructive hover:bg-destructive/10 rounded-lg shrink-0"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>

                        {pm.method && pm.method !== 'EFECTIVO' && (
                          <input
                            type="text"
                            value={pm.reference ?? ''}
                            onChange={e => updatePaymentMethod(index, 'reference', e.target.value)}
                            maxLength={255}
                            placeholder="N° referencia / comprobante (opcional)"
                            className="block w-full rounded-lg border border-input bg-background px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
                          />
                        )}
                      </div>
                    ))}

                    {/* Resumen de totales */}
                    <div className="overflow-hidden rounded-xl border border-border mt-1">
                      <div className="flex items-center justify-between px-4 py-2.5 bg-muted/40">
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Total ingresado</span>
                        <span className={`text-sm font-bold tabular-nums ${
                          amountsMatch ? 'text-amount' : 'text-destructive'
                        }`}>
                          {formatCurrency(totalPaymentAmount, installment.currency)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between px-4 py-2.5">
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Requerido</span>
                        <span className="text-sm font-semibold tabular-nums text-foreground">
                          {formatCurrency(requiredAmount, installment.currency)}
                        </span>
                      </div>
                      {!amountsMatch && totalPaymentAmount > 0 && (
                        <div className="flex items-center justify-between px-4 py-2.5 border-t border-destructive/20 bg-destructive/5">
                          <span className="text-xs font-medium text-destructive uppercase tracking-wider">Diferencia</span>
                          <span className="text-sm font-bold tabular-nums text-destructive">
                            {formatCurrency(Math.abs(totalPaymentAmount - requiredAmount), installment.currency)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          <div className="border-t border-border" />

          {/* ══ Información adicional ════════════════════════════════════ */}
          <div>
            <SectionHeader icon={FileText} title="Información Adicional" />
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-foreground sm:text-sm">Percepciones</Label>
                <MoneyInput
                  currency={installment.currency}
                  value={perceptions !== null ? perceptions.toString() : ''}
                  onValueChange={value => setPerceptions(value)}
                  placeholder="0,00"
                />
                <p className="text-xs text-muted-foreground">Ej: IIBB u otras percepciones.</p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="comments" className="text-xs font-medium text-foreground sm:text-sm">
                  Comentarios
                </Label>
                <Textarea
                  id="comments"
                  value={comments}
                  onChange={e => setComments(e.target.value)}
                  placeholder="Observaciones adicionales..."
                  size="sm"
                  maxLength={255}
                />
                <CharCounter value={comments} max={255} />
              </div>
            </div>
          </div>

          {/* ── Error ── */}
          {error && (
            <div className="flex items-start gap-2.5 rounded-xl border border-destructive/30 bg-destructive/5 p-3.5">
              <AlertCircle className="h-4 w-4 shrink-0 text-destructive mt-0.5" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* ── Footer ── */}
          <div className="flex flex-col-reverse gap-3 border-t border-border pt-4 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
              className="w-full rounded-lg sm:w-auto"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-lg bg-primary text-primary-foreground hover:opacity-90 sm:w-auto min-w-[150px]"
            >
              {loading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Procesando...</>
              ) : (
                <><CheckCircle className="h-4 w-4 mr-2" />Confirmar Cobro</>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
    </>
  );
}
